import {getField, updateField} from 'vuex-map-fields';
import Vuetify from '@/plugins/vuetify';
import Host from '@/types/Host';
import i18n from '@/i18n';
import GlobalEvents from '@/logic/GlobalEvents';
import LanguageHandler from '@/localisation/LanguageHandler';

// Default host
// Used on login page
const defaultHost: Host = {
    id: 0,
    name: "opera.guru",
    pin: 0,
    background_color: "#0000ff",
    body_font_color: "#ffffff",
    font_color: "#ffffff",
    header_color: "#2A8A5A",
    font: 'Catamaran'
};



export const settings = {
    namespaced: true,
    state: {
        hydrated: false,
        inPerformance: false,
        locale: 'en',
        performance: 0,
        profile: 1,
        host: defaultHost,
        dark_mode: true,
        show_timestamps: false,
        show_media: true,
        order_top_first: false,
        performance_display_type: 0,
        large_text: false,
        apiUrl: "https://operaguru.cosy.univie.ac.at",
        wakeLockAvailable: false,
        wakeLockEnabled: true,
        randomBool: false,
        tickLabels: {
            1: 'Small',
            1.5: 'Medium',
            2: 'Large',
        },
        text_size: 1.5,
    },
    getters: {
        isHydrated: (state: any) => state.hydrated,
        inPerformance: (state: any) => state.inPerformance,
        getLocale: (state: any) => state.locale,
        getProfile: (state: any) => state.profile,
        getPerformance: (state: any) => state.performance,
        getHost: (state: any) => state.host,
        isDarkMode: (state: any) => state.dark_mode,
        getField,
        getTextSize: (state: any) => state.text_size,
        getDIColors: (state: any) => {
            const chunkc = state.host.header_color;

            if (chunkc) {

                // Invert colour for partition
                const pred   = 255 - parseInt(chunkc.slice(1, 3), 16);
                const pgreen = 255 - parseInt(chunkc.slice(3, 5), 16);
                const pblue  = 255 - parseInt(chunkc.slice(5, 7), 16);
                const partitionc = '#' + pred.toString(16) + pgreen.toString(16) + pblue.toString(16);

                return {
                    chunk: chunkc,
                    partition: partitionc
                };
            } else {
                return {
                    chunk: '#000000',
                    partition: '#000000'
                };
            }
        },
        useLargeText: (state: any) => state.large_text,
        getApiUrl: (state: any) => state.getApiUrl,
        getWakeLockAvailable: (state: any) => state.wakeLockAvailable,
        getWakeLockEnabled: (state: any) => state.wakeLockEnabled,
        getRandomBool: (state: any) => state.randomBool
    },
    actions: {
        setDarkMode(context: any, val: boolean) {
            Vuetify.framework.theme.dark = val;
            context.commit('setDarkMode', val);
        },
        setLocale(context: any, lang: string) {
            if (i18n.availableLocales.includes(lang)) {
                i18n.locale = lang;
                context.commit('setLocale', lang);
            } else {
                context.dispatch('analytics/logWarning', {
                    message: `Locale not present: ${lang}`,
                    details: JSON.stringify(i18n.availableLocales)
                }, {root: true});
            }
        },
        setPerformance(context: any, id: number) {

            if (context.state.performance !== id) {

                // Invalidate display items
                context.commit('performance/clearDisplayItems', null, { root: true });

                context.commit('setPerformance', id);
            }
        },
        setHost(context: any, host: object) {
            context.commit('setHost', host);
        },
        clearHost(context: any) {
            context.commit('setHost', defaultHost);
        },
        setProfile(context: any, profile: string) {
            context.commit('setProfile', parseInt(profile, 10));
        }
    },
    mutations: {
        setHydrated(state: any) {
            state.hydrated = true;
        },
        setInPerformance(state: any, val: boolean) {
            state.inPerformance = val;
        },
        setLocale(state: any, lang: string) {
            state.locale = lang;
        },
        setPerformance(state: any, id: number) {
            state.performance = id;
        },
        setHost(state: any, host: object) {
            state.host = host;
        },
        setDarkMode(state: any, val: boolean) {
            state.dark_mode = val;
        },
        setProfile(state: any, profile: number) {
            state.profile = profile;
        },
        setWakeLockAvailable(state: any, val: boolean) {
            state.wakeLockAvailable = val;
        },
        setRandomBool(state: any, val: boolean) {
            state.randomBool = val;
        },
        updateField,
    },
};

const firstTimeInit = (store: any) => {
    // Should already have been set at hydrating itself, only for first time
    store.commit('settings/setHydrated');
    const lang = LanguageHandler.getDefaultLocale();
    if (lang) {
        store.commit('settings/setLocale', lang);
    }

    // Binary random number
    const r = Math.floor(Math.random() * 2);

    if (r === 0) {
        store.commit('settings/setRandomBool', true);
    } else {
        store.commit('settings/setRandomBool', false);
    }
};

// Some settings require action to be activated at application loading
export const applySettingsAtLoad = (store: any) => {

    if (!store.getters['settings/isHydrated']) {
        firstTimeInit(store);
    }

    // Apply dark mode option
    Vuetify.framework.theme.dark = store.getters['settings/isDarkMode'];

    // Set locale
    i18n.locale = store.getters['settings/getLocale'];

    // Register event listeners
    GlobalEvents.$on('enter-performance', () => {
        store.commit('settings/setInPerformance', true);
    });
    GlobalEvents.$on('leave-performance', () => {
        store.commit('settings/setInPerformance', false);
    });
};
